'use client'

import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/routing'
import { useConverterStore } from '@/stores/use-converter-store'
import { FileDropZone } from '@/components/shared/FileDropZone'
import { ToolStepIndicator } from '@/components/shared/ToolStepIndicator'
import { trackToolEvent } from '@/lib/utils/analytics'
import type { FileKind } from '@/stores/create-tool-store'

function detectFileKind(file: File): FileKind {
    const nameLower = file.name.toLowerCase()
    if (file.type.startsWith('image/') || /\.(jpg|jpeg|jfif|png|webp|gif|bmp|tif|tiff|ico|heic|heif|svg|avif)$/.test(nameLower)) return 'image'
    if (file.type.startsWith('video/') || /\.(mp4|mkv|webm|mov|avi|flv)$/.test(nameLower)) return 'video'
    if (file.type.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac|aac|opus|wma)$/.test(nameLower)) return 'audio'
    return 'unknown'
}

function getDefaultFormat(kind: FileKind): string {
    if (kind === 'image') return 'webp'
    if (kind === 'video') return 'mp4'
    if (kind === 'audio') return 'mp3'
    return ''
}

export function ConverterUploadStep({ hideStepIndicator }: { hideStepIndicator?: boolean }) {
    const t = useTranslations('Upload')
    const router = useRouter()
    const addFiles = useConverterStore(s => s.addFiles)
    const reset = useConverterStore(s => s.reset)
    const setFileKind = useConverterStore(s => s.setFileKind)
    const setTargetFormat = useConverterStore(s => s.setTargetFormat)

    const handleFilesAdded = (files: File[]) => {
        reset()

        let determinedKind: FileKind = 'unknown'
        let acceptedCount = 0

        addFiles(files, (f) => {
            const currentKind = detectFileKind(f)
            if (currentKind === 'unknown') return false

            if (determinedKind === 'unknown') {
                determinedKind = currentKind
                setFileKind(currentKind)
                setTargetFormat(getDefaultFormat(currentKind))
            }
            // Only add files of the same kind
            const accepted = currentKind === determinedKind
            if (accepted) acceptedCount++
            return accepted
        })

        if (acceptedCount > 0) {
            trackToolEvent('tool_upload_started', {
                tool: 'converter',
                file_count: acceptedCount,
                file_kind: determinedKind,
            })
        }

        router.push('/converter/configurar')
    }

    return (
        <div className="mx-auto max-w-4xl">
            {!hideStepIndicator && <ToolStepIndicator currentStep={1} baseUrl="/converter" />}
            <div className="rounded-3xl bg-white p-4 shadow-2xl shadow-slate-200/50">
                <FileDropZone
                    accept="image/*,video/*,audio/*,.heic,.heif,.svg,.avif,.jfif,.tif,.tiff,.opus,.wma"
                    icon="auto_awesome"
                    title={t('selectFiles')}
                    subtitle={t('subtitleConverter')}
                    onFilesAdded={handleFilesAdded}
                />
            </div>
        </div>
    )
}
